import React, { Component } from "react";
import DatePicker from "react-datetime";
import moment from "moment";

export const toLocalDateInput = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const timezoneOffset = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 10);
};

const toLocalMidnight = (value) => new Date(`${value}T00:00:00`);

const isPositiveInteger = (value) => {
  const numberValue = Number(value);
  return Number.isInteger(numberValue) && numberValue > 0;
};

export const validatePetrolAmountForm = (data) => {
  const errors = {};
  const effectiveDate = toLocalMidnight(data.effective_date);
  const isValidEffectiveDate =
    /^\d{4}-\d{2}-\d{2}$/.test(data.effective_date) &&
    !Number.isNaN(effectiveDate.getTime()) &&
    toLocalDateInput(effectiveDate) === data.effective_date;

  if (!isValidEffectiveDate) {
    errors.effective_date = "Please provide a valid effective date.";
  }
  if (!isPositiveInteger(data.amount_per_km_car)) {
    errors.amount_per_km_car = "Car amount must be a positive integer.";
  }
  if (!isPositiveInteger(data.amount_per_km_bike)) {
    errors.amount_per_km_bike = "Bike amount must be a positive integer.";
  }
  if (typeof data.is_active !== "boolean") {
    errors.is_active = "Please provide the active status.";
  }

  return errors;
};

export const buildPetrolAmountPayload = (data) => ({
  effective_date: toLocalMidnight(data.effective_date).toISOString(),
  amount_per_km_car: Number(data.amount_per_km_car),
  amount_per_km_bike: Number(data.amount_per_km_bike),
  is_active: data.is_active,
});

export default class PetrolAmountForm extends Component {
  constructor(props) {
    super(props);
    const data = props.data;

    this.state = {
      effective_date: toLocalDateInput(data?.effective_date),
      amount_per_km_car: data ? String(data.amount_per_km_car) : "",
      amount_per_km_bike: data ? String(data.amount_per_km_bike) : "",
      is_active: data ? Boolean(data.is_active) : true,
      errors: {},
    };
  }

  handleInputChange = (event) => {
    const { id, type, checked, value } = event.target;
    this.setState({
      [id]: type === "checkbox" ? checked : value,
      errors: {
        ...this.state.errors,
        [id]: undefined,
      },
    });
  };

  handleEffectiveDateChange = (value) => {
    const effectiveDate =
      moment.isMoment(value) && value.isValid()
        ? value.format("YYYY-MM-DD")
        : typeof value === "string"
        ? value
        : "";

    this.setState({
      effective_date: effectiveDate,
      errors: {
        ...this.state.errors,
        effective_date: undefined,
      },
    });
  };

  handleSubmit = (event) => {
    event.preventDefault();
    const data = {
      effective_date: this.state.effective_date,
      amount_per_km_car: this.state.amount_per_km_car,
      amount_per_km_bike: this.state.amount_per_km_bike,
      is_active: this.props.isEdit ? this.state.is_active : true,
    };
    const errors = validatePetrolAmountForm(data);

    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return;
    }

    this.props.onSave(buildPetrolAmountPayload(data));
  };

  preventNonIntegerInput = (event) => {
    if (["e", "E", "+", "-", "."].includes(event.key)) {
      event.preventDefault();
    }
  };

  render() {
    const { isEdit, isSaving } = this.props;
    const {
      effective_date,
      amount_per_km_car,
      amount_per_km_bike,
      is_active,
      errors,
    } = this.state;

    return (
      <div className="container" style={{ marginTop: 20 }}>
        <form id="petrol-amount-form" onSubmit={this.handleSubmit}>
          <div className="row">
            <div className="form-group col-md-6">
              <label htmlFor="effective_date">
                Effective Date<span className="text-danger">*</span>
              </label>
              <DatePicker
                dateFormat="YYYY-MM-DD"
                timeFormat={false}
                value={effective_date}
                onChange={this.handleEffectiveDateChange}
                closeOnSelect
                inputProps={{
                  id: "effective_date",
                  className: "form-control input-md",
                  disabled: isEdit,
                  placeholder: "YYYY-MM-DD",
                  style: {
                    borderColor: errors.effective_date ? "red" : undefined,
                    height: 34,
                    padding: "6px 12px",
                    lineHeight: "1.42857143",
                  },
                }}
              />
              {errors.effective_date && (
                <span className="text-danger">{errors.effective_date}</span>
              )}
            </div>

            <div className="form-group col-md-6">
              <label htmlFor="amount_per_km_car">
                Amount per km (Car)<span className="text-danger">*</span>
              </label>
              <input
                type="number"
                id="amount_per_km_car"
                className="form-control input-md"
                min="1"
                step="1"
                value={amount_per_km_car}
                onChange={this.handleInputChange}
                onKeyDown={this.preventNonIntegerInput}
                disabled={isEdit}
                style={{
                  borderColor: errors.amount_per_km_car ? "red" : undefined,
                }}
              />
              {errors.amount_per_km_car && (
                <span className="text-danger">
                  {errors.amount_per_km_car}
                </span>
              )}
            </div>
          </div>

          <div className="row">
            <div className="form-group col-md-6">
              <label htmlFor="amount_per_km_bike">
                Amount per km (Bike)<span className="text-danger">*</span>
              </label>
              <input
                type="number"
                id="amount_per_km_bike"
                className="form-control input-md"
                min="1"
                step="1"
                value={amount_per_km_bike}
                onChange={this.handleInputChange}
                onKeyDown={this.preventNonIntegerInput}
                disabled={isEdit}
                style={{
                  borderColor: errors.amount_per_km_bike ? "red" : undefined,
                }}
              />
              {errors.amount_per_km_bike && (
                <span className="text-danger">
                  {errors.amount_per_km_bike}
                </span>
              )}
            </div>

            {isEdit && (
              <div className="form-group col-md-6">
                <label
                  style={{
                    cursor: "pointer",
                    marginTop: 30,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={is_active}
                    onChange={this.handleInputChange}
                    style={{ margin: 0 }}
                  />
                  Active
                </label>
                {errors.is_active && (
                  <div className="text-danger">{errors.is_active}</div>
                )}
              </div>
            )}
          </div>

          <div className="row" style={{ marginBottom: 100 }}>
            <div className="col-md-12 btn-rightend">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : isEdit ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </form>
      </div>
    );
  }
}
